/**
 * SetupChecklist — an admin-only slide-over that guides a CRM study from empty
 * to recruiting-ready. Auto-detected items (protocol uploaded, prescreen
 * published, recruiters assigned, …) reflect live CRM data; best-practice steps
 * are manual ticks. Progress persists in studies.config.setup_checklist so the
 * setup can be paused and resumed.
 *
 * It auto-opens once after a study is created (via the ?setup=1 param on
 * StudyDetail) and is reopenable anytime from the header progress chip. Items
 * deep-link to the tab/route that completes them; "Set to Recruiting" is a
 * one-click status flip.
 *
 * Self-fetching: it owns GET/PUT /studies/:id/setup and reports progress up via
 * onProgress so the header chip stays in sync. A reloadKey bump (e.g. when
 * extraction/generation finishes) forces a refetch.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Circle, ClipboardCheck, Loader2, PartyPopper, RotateCcw, Shield, ShieldCheck, X,
} from 'lucide-react';
import api from '../../../services/api';
import { SetupChecklistItem, SetupChecklistResponse } from '../../types';
import { Z, touchTarget } from '../ui';

export interface SetupProgress {
  done: number;
  total: number;
  requiredDone: number;
  requiredTotal: number;
  verified: number;
  requiredVerified: number;
  completed: boolean;
}

interface SetupChecklistProps {
  studyId: string;
  open: boolean;
  onClose: () => void;
  /** Switch the study-detail tab (and close the panel) for a 'tab' action. */
  onNavigateTab: (tab: string) => void;
  /** One-click status flip for the set_recruiting item; resolves when saved. */
  onSetStatus: (status: string) => Promise<void>;
  /** Surfaced so StudyDetail can render the header progress chip. */
  onProgress?: (p: SetupProgress | null) => void;
  /** Bump to force a refetch (e.g. extraction/generation just finished). */
  reloadKey?: number;
}

const errMsg = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Request failed';

export default function SetupChecklist({
  studyId, open, onClose, onNavigateTab, onSetStatus, onProgress, reloadKey = 0,
}: SetupChecklistProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<SetupChecklistResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);

  // Keep the latest onProgress without retriggering load() when the parent
  // re-renders with a new callback identity.
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;

  const report = useCallback((d: SetupChecklistResponse | null) => {
    if (!progressRef.current) return;
    progressRef.current(
      d ? { ...d.progress, completed: Boolean(d.draft.completed_at) } : null
    );
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/api/crm/studies/${studyId}/setup`);
      setData(res.data);
      report(res.data);
      setError(null);
    } catch (e: any) {
      if (!silent) setError(errMsg(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [studyId, report]);

  // Initial load + whenever the parent forces a refetch.
  useEffect(() => { load(); }, [load, reloadKey]);
  // Refresh when the drawer is opened so the data is current the moment it shows.
  useEffect(() => { if (open) load(true); }, [open, load]);

  const persist = useCallback(
    async (body: { checks?: Record<string, boolean>; completed?: boolean; verified?: Record<string, boolean> }) => {
      const res = await api.put(`/api/crm/studies/${studyId}/setup`, body);
      return res.data?.draft;
    },
    [studyId]
  );

  const toggleManual = async (item: SetupChecklistItem) => {
    if (!data || busyKey) return;
    // Auto-detected/in-flight items reflect real data — not manually toggleable.
    if (item.source === 'auto' || item.status === 'in_progress') return;
    const nextChecks = { ...data.draft.checks, [item.key]: item.status !== 'done' };
    setBusyKey(item.key);
    setError(null);
    try {
      await persist({ checks: nextChecks });
      await load(true); // authoritative recompute (statuses + progress)
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setBusyKey(null);
    }
  };

  // Human sign-off: a stronger claim than the auto/AI "done" signal. Only a done
  // item can be verified; toggling re-records or clears the sign-off.
  const toggleVerify = async (item: SetupChecklistItem) => {
    if (!data || busyKey || item.status !== 'done') return;
    setBusyKey(item.key);
    setError(null);
    try {
      await persist({ verified: { [item.key]: !item.verified } });
      await load(true); // authoritative recompute (verified flags + progress)
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setBusyKey(null);
    }
  };

  const runAction = async (item: SetupChecklistItem) => {
    const { action } = item;
    if (action.type === 'tab' && action.value) {
      onNavigateTab(action.value);
    } else if (action.type === 'route' && action.value) {
      onClose();
      navigate(action.value);
    } else if (action.type === 'set_status' && action.value) {
      setBusyKey(item.key);
      setError(null);
      try {
        await onSetStatus(action.value);
        await load(true);
      } catch (e: any) {
        setError(errMsg(e));
      } finally {
        setBusyKey(null);
      }
    }
  };

  const setCompleted = async (completed: boolean) => {
    setCompleting(true);
    setError(null);
    try {
      await persist({ completed });
      await load(true);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setCompleting(false);
    }
  };

  if (!open) return null;

  const progress = data?.progress;
  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const completed = Boolean(data?.draft.completed_at);

  return (
    <div className={`fixed inset-0 ${Z.modalRaised} flex justify-end`} role="dialog" aria-modal="true" aria-label="Study setup checklist">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl animate-slide-in-right dark:bg-gray-800 sm:max-w-lg">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
              <ClipboardCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              Study setup checklist
            </h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Everything this study needs to run correctly. Your progress saves automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close checklist"
            className="-mr-1 -mt-1 rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress bar */}
        {progress && (
          <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700 sm:px-5">
            <div className="mb-1.5 flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {progress.done} of {progress.total} done
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {progress.requiredDone}/{progress.requiredTotal} required
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-all duration-300 ${completed ? 'bg-green-500' : 'bg-primary-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-1.5 flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
              <ShieldCheck className="h-3.5 w-3.5" />
              {progress.verified} of {progress.done} done step{progress.done === 1 ? '' : 's'} human-verified
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          {completed && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
              <PartyPopper className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-300">Setup marked complete.</p>
                <button
                  type="button"
                  onClick={() => setCompleted(false)}
                  disabled={completing}
                  className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:underline disabled:opacity-50 dark:text-green-400"
                >
                  <RotateCcw className="h-3 w-3" /> Reopen checklist
                </button>
              </div>
            </div>
          )}

          {loading && !data ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
            </div>
          ) : data ? (
            <div className="space-y-5">
              {data.groups.map((group) => {
                const items = data.items.filter((i) => i.group === group.key);
                if (items.length === 0) return null;
                return (
                  <section key={group.key}>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      {group.label}
                    </h3>
                    <ul className="space-y-2">
                      {items.map((item) => (
                        <ChecklistRow
                          key={item.key}
                          item={item}
                          busy={busyKey === item.key}
                          onToggle={() => toggleManual(item)}
                          onAction={() => runAction(item)}
                          onVerify={() => toggleVerify(item)}
                        />
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {data && !completed && (
          <div className="border-t border-gray-100 p-4 dark:border-gray-700 sm:px-5">
            <button
              type="button"
              onClick={() => setCompleted(true)}
              disabled={completing}
              className={`inline-flex w-full ${touchTarget} items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 dark:focus:ring-offset-gray-800`}
            >
              {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {progress && progress.requiredDone < progress.requiredTotal ? 'Mark setup complete anyway' : 'Mark setup complete'}
            </button>
            {progress && progress.requiredDone < progress.requiredTotal && (
              <p className="mt-2 text-center text-xs text-gray-400 dark:text-gray-500">
                {progress.requiredTotal - progress.requiredDone} required step
                {progress.requiredTotal - progress.requiredDone === 1 ? '' : 's'} still open
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ChecklistRow({
  item, busy, onToggle, onAction, onVerify,
}: {
  item: SetupChecklistItem;
  busy: boolean;
  onToggle: () => void;
  onAction: () => void;
  onVerify: () => void;
}) {
  const done = item.status === 'done';
  const inProgress = item.status === 'in_progress';
  const hasAction = item.action.type !== 'none';
  const toggleable = !inProgress && item.source !== 'auto';
  const verified = item.verified;

  // An item may name its own button (the texting-agent step: "Open agent
  // settings"); every other item keeps its default.
  const actionLabel = item.action.label
    || (item.action.type === 'set_status' ? 'Set to Recruiting'
      : item.action.type === 'route' ? 'Open builder'
        : 'Go there');

  const verifiedOn = fmtVerifiedDate(item.verifiedAt);

  return (
    <li
      className={`rounded-lg border p-3 transition-colors duration-150 ${
        verified
          ? 'border-primary-200 bg-primary-50/40 dark:border-primary-900/40 dark:bg-primary-900/10'
          : done
            ? 'border-green-200 bg-green-50/50 dark:border-green-900/40 dark:bg-green-900/10'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onToggle}
          disabled={!toggleable || busy}
          aria-label={done ? `${item.label} (done)` : `Mark "${item.label}" done`}
          title={
            item.source === 'auto'
              ? 'Detected automatically from your study data'
              : toggleable
                ? (done ? 'Mark as not done' : 'Mark as done')
                : undefined
          }
          className={`mt-0.5 shrink-0 rounded-full transition-colors duration-150 ${
            toggleable && !busy ? 'cursor-pointer' : 'cursor-default'
          } focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800`}
        >
          {busy ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
          ) : inProgress ? (
            <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
          ) : done ? (
            <CheckCircle2
              className={`h-5 w-5 ${
                item.source === 'auto' ? 'text-green-600 dark:text-green-400' : 'text-primary-600 dark:text-primary-400'
              }`}
            />
          ) : (
            <Circle className="h-5 w-5 text-gray-300 hover:text-gray-400 dark:text-gray-600 dark:hover:text-gray-500" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`text-sm font-medium ${done ? 'text-gray-500 line-through dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
              {item.label}
            </span>
            {item.required && !done && (
              <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                Required
              </span>
            )}
            {inProgress && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                In progress
              </span>
            )}
            {done && item.source === 'auto' && !verified && (
              <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                Detected
              </span>
            )}
            {verified && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                <ShieldCheck className="h-2.5 w-2.5" /> Human-verified
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{item.description}</p>

          {!done && hasAction && (
            <button
              type="button"
              onClick={onAction}
              disabled={busy}
              className="mt-2 inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50"
            >
              {actionLabel} <ArrowRight className="h-3 w-3" />
            </button>
          )}

          {/* Human sign-off — a stronger claim than the automatic/AI check. */}
          {done && (
            <button
              type="button"
              onClick={onVerify}
              disabled={busy}
              title={
                verified
                  ? `Human-verified${verifiedOn ? ` on ${verifiedOn}` : ''}. Click to remove.`
                  : 'Confirm a person reviewed this — a stronger check than the automatic detection.'
              }
              className={
                verified
                  ? 'mt-2 inline-flex items-center gap-1 rounded-md border border-primary-300 bg-primary-600 px-2.5 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 dark:focus:ring-offset-gray-800'
                  : 'mt-2 inline-flex items-center gap-1 rounded-md border border-primary-200 bg-white px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-primary-800 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-primary-900/30'
              }
            >
              {verified ? (
                <><ShieldCheck className="h-3 w-3" /> Verified{verifiedOn ? ` · ${verifiedOn}` : ''}</>
              ) : (
                <><Shield className="h-3 w-3" /> Mark human-verified</>
              )}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/** Short, locale-friendly date for a verification sign-off (e.g. "Jun 30"). */
function fmtVerifiedDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
